/**
 * TopBar - Persistent top bar above all panels (Slack-style)
 *
 * Layout: [Sidebar] [Menu] [Back] [Forward] [Workspace selector] ... [Browser strip] [+] [Help]
 *
 * Fixed at top of window, 48px tall.
 * macOS: offset left to avoid stoplight controls.
 */

import * as Icons from "lucide-react"
import { RefreshCw } from "lucide-react"
import { Tooltip, TooltipTrigger, TooltipContent } from "@craft-agent/ui"
import { OriginRelaySymbol } from "../icons/OriginRelaySymbol"
import { PanelLeftRounded } from "../icons/PanelLeftRounded"
import { TopBarButton } from "../ui/TopBarButton"
import { isMac, isIPad } from "@/lib/platform"
import { useActionLabel } from "@/actions"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuShortcut,
  DropdownMenuSub,
  StyledDropdownMenuContent,
  StyledDropdownMenuItem,
  StyledDropdownMenuSeparator,
  StyledDropdownMenuSubTrigger,
  StyledDropdownMenuSubContent,
} from "@/components/ui/styled-dropdown"
import {
  EDIT_MENU,
  VIEW_MENU,
  SETTINGS_ITEMS,
  getShortcutDisplay,
} from "@/shared/menu-schema"
import type { MenuItem, MenuSection, SettingsMenuItem } from "@/shared/menu-schema"
import { SETTINGS_ICONS } from "../icons/SettingsIcons"
import { SquarePenRounded } from "../icons/SquarePenRounded"
import { useCallback, useEffect, useRef, useState } from "react"
import type { Workspace } from "@/shared/types"
import { WorkspaceSwitcher } from "./WorkspaceSwitcher"
import { cn } from "@/lib/utils"

/** Open a URL using electronAPI with fallback to window.open for touch devices */
function safeOpenUrl(url: string) {
  window.electronAPI?.openUrl?.(url) ?? window.open(url, '_blank', 'noopener,noreferrer')
}

// --- Menu rendering (moved from AppMenu) ---

type MenuActionHandlers = {
  toggleFocusMode?: () => void
  toggleSidebar?: () => void
}

const roleHandlers: Record<string, () => void> = {
  undo: () => window.electronAPI?.menuUndo(),
  redo: () => window.electronAPI?.menuRedo(),
  cut: () => window.electronAPI?.menuCut(),
  copy: () => window.electronAPI?.menuCopy(),
  paste: () => window.electronAPI?.menuPaste(),
  selectAll: () => window.electronAPI?.menuSelectAll(),
  zoomIn: () => window.electronAPI?.menuZoomIn(),
  zoomOut: () => window.electronAPI?.menuZoomOut(),
  resetZoom: () => window.electronAPI?.menuZoomReset(),
  minimize: () => window.electronAPI?.menuMinimize(),
  zoom: () => window.electronAPI?.menuMaximize(),
}

function getIcon(name: string): React.ComponentType<{ className?: string }> | null {
  const IconComponent = Icons[name as keyof typeof Icons] as React.ComponentType<{ className?: string }> | undefined
  return IconComponent ?? null
}

function renderMenuItem(
  item: MenuItem,
  index: number,
  actionHandlers: MenuActionHandlers
): React.ReactNode {
  if (item.type === 'separator') {
    return <StyledDropdownMenuSeparator key={`sep-${index}`} />
  }

  const Icon = getIcon(item.icon)
  const shortcut = getShortcutDisplay(item, isMac)

  if (item.type === 'role') {
    const handler = roleHandlers[item.role]
    const safeHandler = handler ?? (() => {
      console.warn(`[TopBar] No handler registered for role: ${item.role}`)
    })
    return (
      <StyledDropdownMenuItem key={item.role} onClick={safeHandler}>
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {item.label}
        {shortcut && <DropdownMenuShortcut className="pl-6">{shortcut}</DropdownMenuShortcut>}
      </StyledDropdownMenuItem>
    )
  }

  if (item.type === 'action') {
    const handler = item.id === 'toggleFocusMode'
      ? actionHandlers.toggleFocusMode
      : item.id === 'toggleSidebar'
        ? actionHandlers.toggleSidebar
        : undefined
    return (
      <StyledDropdownMenuItem key={item.id} onClick={handler}>
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {item.label}
        {shortcut && <DropdownMenuShortcut className="pl-6">{shortcut}</DropdownMenuShortcut>}
      </StyledDropdownMenuItem>
    )
  }

  return null
}

function renderMenuSection(
  section: MenuSection,
  actionHandlers: MenuActionHandlers
): React.ReactNode {
  const Icon = getIcon(section.icon)
  return (
    <DropdownMenuSub key={section.id}>
      <StyledDropdownMenuSubTrigger>
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {section.label}
      </StyledDropdownMenuSubTrigger>
      <StyledDropdownMenuSubContent>
        {section.items.map((item, index) => renderMenuItem(item, index, actionHandlers))}
      </StyledDropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

// --- TopBar ---

interface TopBarProps {
  workspaces: Workspace[]
  activeWorkspaceId: string | null
  onSelectWorkspace: (workspaceId: string, openInNewWindow?: boolean) => void
  workspaceUnreadMap?: Record<string, boolean>

  onNewChat: () => void
  onNewWindow?: () => void
  onOpenSettings: () => void
  onOpenSettingsSubpage: (subpage: SettingsMenuItem['id']) => void
  onOpenKeyboardShortcuts: () => void
  onOpenStoredUserPreferences: () => void
  onBack: () => void
  onForward: () => void
  canGoBack: boolean
  canGoForward: boolean
  onToggleSidebar: () => void
  onToggleFocusMode: () => void
  onAddSessionPanel: () => void
  onAddBrowserPanel: () => void
  onRefresh: () => void
  layoutMode?: 'mobile' | 'tablet' | 'desktop'
  mobileView?: 'list' | 'chat'
  onOpenSidebarDrawer?: () => void
  onMobileBack?: () => void
  mobileTitle?: string
}

export function TopBar({
  workspaces,
  activeWorkspaceId,
  onSelectWorkspace,
  workspaceUnreadMap,
  onNewChat,
  onNewWindow,
  onOpenSettings,
  onOpenSettingsSubpage,
  onOpenKeyboardShortcuts,
  onOpenStoredUserPreferences,
  onBack,
  onForward,
  canGoBack,
  canGoForward,
  onToggleSidebar,
  onToggleFocusMode,
  onAddSessionPanel,
  onAddBrowserPanel,
  onRefresh,
  layoutMode = 'desktop',
  mobileView,
  onOpenSidebarDrawer,
  onMobileBack,
  mobileTitle,
}: TopBarProps) {
  const [isDebugMode, setIsDebugMode] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const rightSlotRef = useRef<HTMLDivElement | null>(null)

  const handleRefresh = useCallback(() => {
    if (isRefreshing) return
    setIsRefreshing(true)
    onRefresh()
    // Show spin for a brief moment so the user gets visual feedback
    setTimeout(() => setIsRefreshing(false), 1000)
  }, [isRefreshing, onRefresh])

  const isMobileLayout = layoutMode === 'mobile'
  const isTabletLayout = layoutMode === 'tablet'
  const isCompactLayout = isMobileLayout || isTabletLayout

  const newChatHotkey = useActionLabel('app.newChat').hotkey
  const newWindowHotkey = useActionLabel('app.newWindow').hotkey
  const settingsHotkey = useActionLabel('app.settings').hotkey
  const keyboardShortcutsHotkey = useActionLabel('app.keyboardShortcuts').hotkey
  const goBackHotkey = useActionLabel('nav.goBackAlt').hotkey
  const goForwardHotkey = useActionLabel('nav.goForwardAlt').hotkey

  useEffect(() => {
    window.electronAPI?.isDebugMode?.()?.then(setIsDebugMode).catch(() => {})
  }, [])

  const actionHandlers: MenuActionHandlers = {
    toggleFocusMode: onToggleFocusMode,
    toggleSidebar: onToggleSidebar,
  }

  // Web app — no macOS traffic-light buttons, always use compact padding
  const menuLeftPadding = 12

  return (
    <div
      className={cn("fixed left-0 right-0 z-panel", !isIPad && "titlebar-drag-region")}
      style={{ top: 'env(safe-area-inset-top, 0px)', height: 'var(--topbar-height)' }}
    >
      <div className="flex h-full w-full items-center justify-between gap-2">
      {/* === LEFT: Sidebar + Menu + Navigation + Workspace === */}
      {/* Keep this container draggable. Only individual interactive controls should use titlebar-no-drag. */}
      <div className="pointer-events-auto flex min-w-0 flex-1 items-center gap-0.5" style={{ paddingLeft: menuLeftPadding }}>

        <div className="flex items-center gap-0.5">
        {isMobileLayout && mobileView === 'chat' ? (
          <TopBarButton onClick={onMobileBack} aria-label="Back to sessions">
            <Icons.ChevronLeft className="h-5 w-5 text-foreground/70" strokeWidth={1.5} />
          </TopBarButton>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <TopBarButton 
                onClick={isCompactLayout ? onOpenSidebarDrawer : onToggleSidebar} 
                aria-label={isCompactLayout ? "Open sidebar" : "Toggle sidebar"}
              >
                {isCompactLayout ? (
                  <Icons.Menu className="h-[18px] w-[18px] text-foreground/70" />
                ) : (
                  <PanelLeftRounded className="h-[18px] w-[18px] text-foreground/70" />
                )}
              </TopBarButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">{isCompactLayout ? "Open Sidebar" : "Toggle Sidebar"}</TooltipContent>
          </Tooltip>
        )}

        {/* App Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <TopBarButton aria-label="Origin Relay menu">
              <OriginRelaySymbol className="h-4 text-foreground" />
            </TopBarButton>
          </DropdownMenuTrigger>
          <StyledDropdownMenuContent align="start" minWidth="min-w-48">
            <StyledDropdownMenuItem onClick={onNewChat}>
              <SquarePenRounded className="h-3.5 w-3.5" />
              New Chat
              {!isIPad && newChatHotkey && <DropdownMenuShortcut className="pl-6">{newChatHotkey}</DropdownMenuShortcut>}
            </StyledDropdownMenuItem>
            {onNewWindow && !isIPad && (
              <StyledDropdownMenuItem onClick={onNewWindow}>
                <Icons.AppWindow className="h-3.5 w-3.5" />
                New Window
                {!isIPad && newWindowHotkey && <DropdownMenuShortcut className="pl-6">{newWindowHotkey}</DropdownMenuShortcut>}
              </StyledDropdownMenuItem>
            )}

            <StyledDropdownMenuSeparator />

            {renderMenuSection(EDIT_MENU, actionHandlers)}
            {renderMenuSection(VIEW_MENU, actionHandlers)}

            <StyledDropdownMenuSeparator />

            <DropdownMenuSub>
              <StyledDropdownMenuSubTrigger>
                <Icons.Settings className="h-3.5 w-3.5" />
                Settings
              </StyledDropdownMenuSubTrigger>
              <StyledDropdownMenuSubContent>
                <StyledDropdownMenuItem onClick={onOpenSettings}>
                  <Icons.Settings className="h-3.5 w-3.5" />
                  Settings...
                  {!isIPad && settingsHotkey && <DropdownMenuShortcut className="pl-6">{settingsHotkey}</DropdownMenuShortcut>}
                </StyledDropdownMenuItem>
                <StyledDropdownMenuSeparator />
                {SETTINGS_ITEMS.map((item) => {
                  const Icon = SETTINGS_ICONS[item.id]
                  return (
                    <StyledDropdownMenuItem
                      key={item.id}
                      onClick={() => onOpenSettingsSubpage(item.id)}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {item.label}
                    </StyledDropdownMenuItem>
                  )
                })}
              </StyledDropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <StyledDropdownMenuSubTrigger>
                <Icons.HelpCircle className="h-3.5 w-3.5" />
                Help
              </StyledDropdownMenuSubTrigger>
              <StyledDropdownMenuSubContent>
                <StyledDropdownMenuItem onClick={() => safeOpenUrl('https://agents.craft.do/docs')}>
                  <Icons.HelpCircle className="h-3.5 w-3.5" />
                  Help & Documentation
                  <Icons.ExternalLink className="h-3 w-3 ml-auto text-muted-foreground" />
                </StyledDropdownMenuItem>
                <StyledDropdownMenuItem onClick={onOpenKeyboardShortcuts}>
                  <Icons.Keyboard className="h-3.5 w-3.5" />
                  Keyboard Shortcuts
                  {!isIPad && keyboardShortcutsHotkey && <DropdownMenuShortcut className="pl-6">{keyboardShortcutsHotkey}</DropdownMenuShortcut>}
                </StyledDropdownMenuItem>
              </StyledDropdownMenuSubContent>
            </DropdownMenuSub>

            {isDebugMode && (
              <>
                <DropdownMenuSub>
                  <StyledDropdownMenuSubTrigger>
                    <Icons.Bug className="h-3.5 w-3.5" />
                    Debug
                  </StyledDropdownMenuSubTrigger>
                  <StyledDropdownMenuSubContent>
                    <StyledDropdownMenuItem onClick={() => window.electronAPI?.checkForUpdates?.()}>
                      <Icons.Download className="h-3.5 w-3.5" />
                      Check for Updates
                    </StyledDropdownMenuItem>
                    <StyledDropdownMenuItem onClick={() => window.electronAPI?.installUpdate?.()}>
                      <Icons.Download className="h-3.5 w-3.5" />
                      Install Update
                    </StyledDropdownMenuItem>
                    <StyledDropdownMenuSeparator />
                    <StyledDropdownMenuItem onClick={() => window.electronAPI?.menuToggleDevTools?.()}>
                      <Icons.Bug className="h-3.5 w-3.5" />
                      Toggle DevTools
                      <DropdownMenuShortcut className="pl-6">{isMac ? '⌥⌘I' : 'Ctrl+Shift+I'}</DropdownMenuShortcut>
                    </StyledDropdownMenuItem>
                  </StyledDropdownMenuSubContent>
                </DropdownMenuSub>
              </>
            )}

            <StyledDropdownMenuSeparator />
            <StyledDropdownMenuItem
              onClick={() => {
                if (window.confirm('Disconnect and clear all cached data?')) {
                  localStorage.clear()
                  sessionStorage.clear()
                  window.location.reload()
                }
              }}
              className="text-destructive"
            >
              <Icons.LogOut className="h-3.5 w-3.5" />
              Disconnect & Reset
            </StyledDropdownMenuItem>

          </StyledDropdownMenuContent>
        </DropdownMenu>
        </div>

        {/* Back / Forward / Workspace selector (moved from center) */}
        {!isMobileLayout && (
        <div className={cn("ml-1 flex min-w-0 items-center gap-1 overflow-hidden", isTabletLayout ? "w-[clamp(120px,25vw,300px)]" : "w-[clamp(180px,30vw,500px)]")}>
          <Tooltip>
            <TooltipTrigger asChild>
              <TopBarButton onClick={onBack} disabled={!canGoBack} aria-label="Go back">
                <Icons.ChevronLeft className="h-[18px] w-[18px] text-foreground/70" strokeWidth={1.5} />
              </TopBarButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">Back {goBackHotkey}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <TopBarButton onClick={onForward} disabled={!canGoForward} aria-label="Go forward">
                <Icons.ChevronRight className="h-[18px] w-[18px] text-foreground/70" strokeWidth={1.5} />
              </TopBarButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">Forward {goForwardHotkey}</TooltipContent>
          </Tooltip>

          {workspaces.length > 1 && (
            <div className="min-w-0 flex-1">
              <WorkspaceSwitcher
                variant="topbar"
                workspaces={workspaces}
                activeWorkspaceId={activeWorkspaceId}
                onSelect={onSelectWorkspace}
                workspaceUnreadMap={workspaceUnreadMap}
              />
            </div>
          )}
        </div>
        )}

        {isMobileLayout && mobileView === 'chat' && mobileTitle && (
          <div className="flex-1 min-w-0 text-center">
            <span className="block text-sm font-medium text-foreground truncate">{mobileTitle}</span>
          </div>
        )}
      </div>

      {/* === RIGHT: Browser strip + add + help === */}
      <div ref={rightSlotRef} className="flex min-w-0 shrink-0 items-center justify-end gap-0.5" style={{ paddingRight: 12 }}>
        
        {!isMobileLayout && !isTabletLayout && (
        <Tooltip>
          <TooltipTrigger asChild>
            <TopBarButton onClick={onAddSessionPanel} aria-label="New session panel" className="ml-1 h-[26px] w-[26px] rounded-lg">
              <Icons.Plus className="h-4 w-4 text-foreground/50" strokeWidth={1.5} />
            </TopBarButton>
          </TooltipTrigger>
          <TooltipContent side="bottom">New Session Panel</TooltipContent>
        </Tooltip>
        )}

        {/* Refresh button — forces a reconnect to sync latest state from the server */}
        <Tooltip>
          <TooltipTrigger asChild>
            <TopBarButton onClick={handleRefresh} aria-label="Refresh" className="h-[26px] w-[26px] rounded-lg">
              <RefreshCw className={cn("h-4 w-4 text-foreground/50", isRefreshing && "animate-spin")} strokeWidth={1.5} />
            </TopBarButton>
          </TooltipTrigger>
          <TooltipContent side="bottom">Refresh</TooltipContent>
        </Tooltip>
      </div>
      </div>
    </div>
  )
}
